import { effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { CustomRouteService } from 'src/app/pages/custom-route/services/custom-route.service';
import { PlannerDayStateService } from '../../../services/planner-day-state.service';
import { TimelineSegment, TimeSegment, SegmentType, SegmentStatus, CardStatus } from '../interfaces';
import { Point } from 'src/app/pages/custom-route/interfaces/custom-route.interface';
import { dailyHours } from '../config/constants';
import { PlannerSelectedDayStateService } from '../../../services';

@Injectable({
  providedIn: 'root'
})
export class RouteTimelineService {
  public now: WritableSignal<Date> = signal<Date>(new Date()); // '2025-06-11T12:30:00'
  public timelineSegments: WritableSignal<TimelineSegment[]> = signal<TimelineSegment[]>([]);
 
  private _customRouteService = inject(CustomRouteService);
  private _plannerDayStateService = inject(PlannerDayStateService);
  private _plannerSelectedDayStateService = inject(PlannerSelectedDayStateService);
  
  private _dayToShowIndex =  this._plannerDayStateService.dayToShowIndex;
  private _customRouteData = this._customRouteService.customRouteData;
  private _selectedDate = this._plannerSelectedDayStateService.selectedDate;

  public dailyHours = dailyHours;
  
  /************************************************************************
   * 
   * Logic for generating the card status based on the selected date, 
   * points and current time
   * 
   ************************************************************************/
  getCardStatusFromSegmentTime(
    point: Point,
    now: Date
  ): CardStatus {
    const start = point.startDate
    const end = point.endDate

    if (now < start) return 'future';
    if (now >= end) return 'past';
    return 'present';
  }
  
  /************************************************************************
   * 
   * Logic for generating the timeline status based on the selected date, 
   * points and current time
   * 
   ************************************************************************/
  setTimelineSegments = effect(() => {
    if (!this._selectedDate()) return;
    
    const route = this._customRouteData()?.dailyRoutes?.[this._dayToShowIndex()];
    if (!route) return;

    const timelineSegments = this.generateTimeline(route.points);
    if (!timelineSegments) return;
    
    this.timelineSegments.set(timelineSegments);
  }, { allowSignalWrites: true });
  
  /**
   * 
   * @param points 
   * @description Generates the timeline segments for the selected date based on the provided points.
   * and current time. It gives the status of each segment based on whether it is in the past, present or future.
   */
  public generateTimeline(points: Point[]): TimelineSegment[] | undefined {
    if (!this._selectedDate() || !points.length) return;

    return this.dailyHours.map(hourStr => {
      const hour = parseInt(hourStr);

      const segments: TimeSegment[] = [
        this.buildSegment(hour, 0, 'big', this.now(), points),
        ...[15].map(min => this.buildSegment(hour, min, 'small', this.now(), points)),
        this.buildSegment(hour, 30, 'medium', this.now(), points),
        ...[45].map(min => this.buildSegment(hour, min, 'small', this.now(), points)),
      ];

      return { hour: hourStr, segments };
    });
  }

  /**
   * 
   * @returns a TimeSegment object representing the segment for the given hour and minute
   * @description Builds a TimeSegment object for the given hour and minute, 
   * determining its type and status based on the current time and the points provided.
   */
  private buildSegment(
    hour: number,
    minute: number,
    type: SegmentType,
    now: Date,
    points: Point[]
  ): TimeSegment {
    const segmentDate = new Date(this._selectedDate()!);
    segmentDate.setHours(hour, minute, 0, 0);

    const status = this.resolveSegmentStatus(segmentDate, now, points);

    return {
      time: segmentDate.toTimeString().slice(0, 5),
      type,
      status,
    };
  }

  /**
   * 
   * @returns the status of the segment based on the current time and the points provided
   * @description Determines if the segment is in the past, present or future based on the current time and the points provided.
   */
  private resolveSegmentStatus(
    segmentDate: Date,
    now: Date,
    points: Point[]
  ): SegmentStatus {
    const isInSameActivePoint = points.some(point => {
      const [start, end] = this.getPointTimeRange(point);
      return this.isInTimeRange(segmentDate, start, end) && this.isInTimeRange(now, start, end);
    });

    if (isInSameActivePoint) return 'present';
    return segmentDate < now ? 'past' : 'future';
  }

  /**
   * 
   * @returns the start and end time of the point segment as a tuple of Date objects
   * @description Builds a date object from the selected date and the segment time of the point.
   */
  private getPointTimeRange(point: Point): [Date, Date] {
    const start = point.startDate;
    const end = point.endDate;
    return [start, end];
  }

  /**
   * 
   * @returns true if the date is within the start and end time range, false otherwise
   * @description Checks if the given date is within the start and end time range.
   */
  private isInTimeRange(date: Date, start: Date, end: Date): boolean {
    return date >= start && date < end;
  }

  
  /***************************************************
   * 
   * Logic for updating the timeline every interval time
   * 
   ***************************************************/

  /**
   * 
   * @returns  a timeout that schedules the next update of the timeline
   * @description Schedules the next update of the timeline at regular intervals between `startHour` and `endHour`.
   * If the current time is outside the update window, it returns undefined.
   */
  public scheduleNextUpdate = (intervalMinutes: number, startHour: number, endHour: number) => {
    const timeUntilNext = this._getTimeUntilNextUpdate(intervalMinutes, startHour, endHour);
    
    // If the time until the next update is -1, it means we are outside the update window
    if (timeUntilNext === -1) {
      console.log('Fuera del horario de actualizaciones');
      return undefined;
    }
    
    return setTimeout(() => {
      this.now.set(new Date());
      console.log('Actualizando el tiempo cada 15 minutos desde las 9:00:', new Date().toLocaleTimeString());
      
      // Program next update
      const interval = setInterval(() => {
        this.now.set(new Date());
        console.log('Actualizando el tiempo cada 15 minutos:', new Date().toLocaleTimeString());
      }, intervalMinutes * 60 * 1000); 
      
      // Clear the interval when the component is destroyed
      return () => clearInterval(interval);
    }, timeUntilNext);
  };
  
  /**
   * Calculates the time in milliseconds until the next update, which occurs at regular intervals
   * between `startHour` and `endHour` on the current day.
   *
   * @param intervalMinutes - Interval in minutes between updates.
   * @param startHour - Starting hour of the update window (0–23).
   * @param endHour - Ending hour of the update window (0–23).
   *
   * @returns 
   * - A positive number: milliseconds until the next scheduled update.
   * - 0 if the next update should happen immediately.
   * - -1 if no more updates will occur today (e.g., current time is past `endHour`).
   */
  private _getTimeUntilNextUpdate = (intervalMinutes: number, startHour: number, endHour: number): number => {
    const now = new Date();

    const todayStartHour = new Date(now.getFullYear(), now.getMonth(), now.getDate(), startHour, 0, 0, 0);
    const todayEndHour = new Date(now.getFullYear(), now.getMonth(), now.getDate(), endHour, 0, 0, 0);
    
    // If the current time is before startHour, return the time until startHour
    if (now < todayStartHour) {
      return todayStartHour.getTime() - now.getTime();
    }
    
    // If the current time is after endHour, no more updates today (return -1 or large number)
    if (now >= todayEndHour) {
      return -1; // o return Infinity; según lo que prefieras
    }
    
    // Calculate the number of minutes since startHour
    const minutesSinceStartHour = (now.getTime() - todayStartHour.getTime()) / (1000 * 60);
    const intervalsPassed = Math.floor(minutesSinceStartHour / intervalMinutes);
    
    // Calculate the next update time
    const nextIntervalMinutes = (intervalsPassed + 1) * intervalMinutes;
    const nextUpdateTime = new Date(todayStartHour.getTime() + nextIntervalMinutes * 60 * 1000);
    
    // Check if the next update would be after endHour
    if (nextUpdateTime >= todayEndHour) {
      return -1; // No more updates today
    }
    
    return nextUpdateTime.getTime() - now.getTime();
  };
}

